import React, { useState } from 'react';
import { Blaetterleiste, TabellenKopf, useTabelle, type SpaltenDefinition } from '../components/TabellenSteuerung';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { orders } from '../api';
import StatusBadge from '../components/StatusBadge';
import type { OrderStatus, OrderType } from '../types';
import { formatDistanceToNow, format } from 'date-fns';
import { useEinstellung } from '../contexts/EinstellungenContext';

const ORDER_STATUSES: OrderStatus[] = [
  'DRAFT', 'PENDING_PAYMENT', 'PAID', 'PENDING_PROVISIONING',
  'PROVISIONING', 'ACTIVE', 'FAILED', 'CANCELLED', 'SCHEDULED', 'COMPLETED',
];

const ORDER_TYPES: OrderType[] = [
  'NEW_INSTANCE', 'UPGRADE', 'DOWNGRADE', 'CANCELLATION', 'ADDON', 'MANUAL_ADJUSTMENT',
];

export default function OrdersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useEinstellung<string>('bestellungen.status', '');
  const [typeFilter, setTypeFilter] = useEinstellung<string>('bestellungen.typ', '');

  const { data, isLoading } = useQuery({
    queryKey: ['orders', { status: statusFilter, type: typeFilter }],
    queryFn: () =>
      orders.list({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      }),
  });

  const provisionMutation = useMutation({
    mutationFn: (id: string) => orders.provision(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderStatus }) => orders.updateStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Statuswechsel fehlgeschlagen.'),
  });

  const rows = data ?? [];

  const spalten: SpaltenDefinition<(typeof rows)[number]>[] = [
    { key: 'kunde', label: t('tables.customer'), wert: (o) => o.customer?.companyName ?? '' },
    { key: 'typ', label: t('tables.type'), wert: (o) => o.type },
    { key: 'status', label: t('common.status'), wert: (o) => o.status },
    { key: 'wc', label: t('tables.wcOrder'), wert: (o) => o.woocommerceOrderId ?? '' },
    { key: 'zahlung', label: t('tables.payment'), wert: (o) => o.paymentStatus ?? '' },
    { key: 'erstellt', label: t('common.createdAt'), wert: (o) => o.createdAt },
    { key: 'bezahlt', label: t('tables.paid'), wert: (o) => o.paidAt },
  ];

  const tabelle = useTabelle({
    speichern: 'bestellungen',
    zeilen: rows,
    spalten,
    startSortierung: { key: 'erstellt', richtung: 'ab' },
  });


  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">{t('filters.allStatuses')}</option>
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">{t('filters.allTypes')}</option>
          {ORDER_TYPES.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <span className="ml-auto text-sm text-slate-500">{rows.length} orders</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <TabellenKopf spalten={spalten} steuerung={tabelle} extra={[t('common.actions')]} />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-200 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-400 italic">{t('empty.orders')}</td>
              </tr>
            ) : (
              tabelle.sichtbar.map((order) => (
                <tr key={order.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {order.customer?.companyName ?? order.customerId.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600">
                      {order.type.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={order.status} type="order" />
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">
                    {order.woocommerceOrderId ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {order.paymentStatus ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {formatDistanceToNow(new Date(order.createdAt), { addSuffix: true })}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {order.paidAt ? format(new Date(order.paidAt), 'PP') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {(order.status === 'PAID' || order.status === 'PENDING_PROVISIONING') && (
                      <button
                        onClick={() => {
                          if (confirm('Provision this order now?')) provisionMutation.mutate(order.id);
                        }}
                        disabled={provisionMutation.isPending}
                        className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 font-medium disabled:opacity-50"
                      >
                        <Zap size={12} /> Provision
                      </button>
                    )}

                    {/* Den Status von Hand setzen.
                        Normalerweise kommt er über den WooCommerce-Webhook. Der
                        fällt aber aus, wenn jemand per Rechnung zahlt, eine
                        Bestellung storniert wird oder der Webhook einmal nicht
                        ankommt — und dann steckt sie fest, ohne dass jemand sie
                        weiterbewegen kann. Bewusst eine Auswahlliste und kein
                        Knopf: welcher Status richtig ist, weiß nur der Mensch. */}
                    <select
                      value=""
                      onChange={(e) => {
                        const neu = e.target.value as OrderStatus;
                        if (!neu) return;
                        if (confirm(t('orders.setStatusConfirm', { status: neu })))
                          statusMutation.mutate({ id: order.id, status: neu });
                        e.target.value = '';
                      }}
                      className="mt-1 block text-xs text-slate-400 bg-transparent hover:text-slate-600 focus:outline-none"
                    >
                      <option value="">{t('orders.setStatus')}</option>
                      {ORDER_STATUSES.filter((s) => s !== order.status).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Blaetterleiste blaettern={tabelle.blaettern} />
    </div>
  );
}
